import { Button, Checkbox, cn, Divider, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader } from '@ds';
import { useState } from 'react';
import { TmdxResourceCard } from '@features/components/ResourceCards';
import { AssignedUser, CaseUserGroup, CaseUserGroupNames, EmployeeLabels } from '@/api/apis/case';
import { roleByCaseUserGroup } from '@/constants/case/case.constants';
import { employeeLabelColors } from '@/constants/resources.constants';
import { EMPLOYEE_ROLE_NAMES, groupsWithMandatoryPrimary, roleNames } from '@/constants/user.constants';
import { getFullName } from '@/utils/helpers';
import { ResourceSection } from './ResourceSection';
import { useUserSettingsStore } from '@/store/user-settings.store';
import { Uuid } from '@/api/types';
import { SharePrimaryIcon } from './SharePrimaryIcon';

type SetIsPrimaryModalProps = {
  isOpen: boolean;
  isPending: boolean;
  primaryAvailableUsers: Partial<Record<CaseUserGroup, AssignedUser[]>>;
  caseId: string;
  selectedEmployeeId?: Uuid;
  selectedGroup?: CaseUserGroup;
  onOpenChange: () => void;
  onSubmit: ({
    assignedUsers,
    usersToUpdate,
    onClose,
  }: {
    assignedUsers: Partial<Record<CaseUserGroup, AssignedUser[]>>;
    usersToUpdate: Partial<Record<CaseUserGroup, AssignedUser[]>>;
    onClose: () => void;
  }) => void;
  onClose: () => void;
};

export const SetIsPrimaryModal = ({
  isOpen,
  isPending,
  primaryAvailableUsers,
  caseId,
  selectedEmployeeId,
  selectedGroup,
  onOpenChange,
  onSubmit,
  onClose,
}: SetIsPrimaryModalProps) => {
  const { addBlockPrimaryModalCaseId } = useUserSettingsStore();
  const [isAgreed, setIsAgreed] = useState<boolean>(false);

  const getInitialPrimary = () => {
    if (selectedGroup) {
      const selectedUser = primaryAvailableUsers[selectedGroup]?.find((u) => u.employeeId === selectedEmployeeId);
      if (selectedUser) return { [selectedGroup]: [{ ...selectedUser, isPrimary: true }] };
    }
    return {};
  };

  const [selectedPrimaries, setSelectedPrimaries] =
    useState<Partial<Record<CaseUserGroup, AssignedUser[]>>>(getInitialPrimary());

  const handleSubmit = () => {
    if (isAgreed) addBlockPrimaryModalCaseId(caseId);
    onSubmit({ assignedUsers: primaryAvailableUsers, usersToUpdate: selectedPrimaries, onClose });
  };

  const handleSelectPrimary = (user: AssignedUser) => {
    if (!user.isPrimary) {
      setSelectedPrimaries((prevState) => ({ ...prevState, [user.group]: [{ ...user, isPrimary: true }] }));
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      size="3xl"
      onOpenChange={onOpenChange}
      scrollBehavior="inside"
      hideCloseButton
      classNames={{
        header: 'text-hl! text-base-10 font-secondary flex flex-col gap-1 bg-base-80 rounded-2xl',
        body: 'bg-base-80 py-4',
        footer: 'bg-base-80 py-3 rounded-2xl',
      }}
    >
      <ModalContent>
        <ModalHeader>
          Choose Primary
          <Divider />
        </ModalHeader>
        <ModalBody>
          <div className="flex gap-6 flex-col">
            <div className="text-m-r">
              {`Some roles have multiple representatives. To improve team communication, please consider designating one
              as the 'primary' contact. This can be done later in the resource card menu.`}
            </div>
            <div className="flex flex-col gap-3">
              {Object.keys(primaryAvailableUsers).map((group) => {
                const groupKey = group as CaseUserGroup;
                const currentUsers = primaryAvailableUsers[groupKey];
                const roleOnCase = roleByCaseUserGroup[groupKey]!;
                return (
                  <ResourceSection key={group} title={CaseUserGroupNames[groupKey]}>
                    <div className="flex flex-col gap-4">
                      {groupsWithMandatoryPrimary.includes(groupKey) && (
                        <div>It is mandatory to specify a Primary {EMPLOYEE_ROLE_NAMES[roleOnCase]}</div>
                      )}
                      <div className="flex gap-4 flex-wrap">
                        {currentUsers &&
                          currentUsers.map((currentUser) => {
                            const { user, isPrimary, userId, employeeId } = currentUser;
                            const fullName = getFullName(user.firstName, user.lastName);
                            const isSelectedInGroup =
                              selectedPrimaries[groupKey] && selectedPrimaries[groupKey]![0].employeeId === employeeId;

                            return (
                              <button
                                key={`${userId || employeeId}_resource`}
                                className="flex w-[318px]! h-[117px]!"
                                onClick={() => handleSelectPrimary(currentUser)}
                                aria-label="select-primary"
                              >
                                <TmdxResourceCard
                                  icon={<SharePrimaryIcon isPrimary={isPrimary} groupKey={groupKey} />}
                                  menuDisabled
                                  showChip={false}
                                  list={[]}
                                  type="user"
                                  title={fullName}
                                  subTitle={roleOnCase ? roleNames[roleOnCase] : ''}
                                  phoneNumber={user.phone}
                                  avatarProps={{
                                    src: user.photoPath,
                                    label: isPrimary ? EmployeeLabels.Primary : undefined,
                                    isStatus: true,
                                    chipProps: {
                                      color: isPrimary ? employeeLabelColors[EmployeeLabels.Primary] : undefined,
                                    },
                                  }}
                                  className={cn('flex w-full bg-white border-0 p-4 pb-0', {
                                    'border-2 border-base-5': isSelectedInGroup,
                                  })}
                                />
                              </button>
                            );
                          })}
                      </div>
                    </div>
                  </ResourceSection>
                );
              })}
            </div>
          </div>
        </ModalBody>
        <ModalFooter className="flex flex-col">
          <Divider />
          <div className="flex gap-3 py-3">
            <Checkbox size="sm" isSelected={isAgreed} onValueChange={setIsAgreed}>
              <span className="text-sm text-m-b">Don’t show this message again for this case?</span>
            </Checkbox>
            <Button disabled={isPending} className="h-9 flex-1" variant="outline" onPress={onOpenChange}>
              Reset to Default
            </Button>
            <Button
              className="h-9 flex-1"
              isLoading={isPending}
              variant="solid"
              onPress={handleSubmit}
              isDisabled={Object.keys(selectedPrimaries).length === 0}
            >
              Save Changes
            </Button>
          </div>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
